import { z } from 'zod';
import { HistoryEntrySchema, IDSchema, IsoTimestampSchema, MsaTemplateVersionSchema, QuoteIdSchema, SchemaVersionSchema, } from './shared.js';
export const DealStateSchema = z.enum([
    'proposalPending',
    'signaturePending',
    'signed',
    'intakeSubmitted',
    'active',
    'renewed',
    'cancelled',
]);
export const PilotSubStateSchema = z.enum(['pilotComplete', 'pilotConverted', 'pilotEnded']);
export const TrialSubStateSchema = z.enum([
    'trialActive',
    'trialComplete',
    'trialConverted',
    'trialEnded',
]);
export const ESignVendorSchema = z.enum(['docusign', 'adobe_sign']);
export const EnvelopeStatusSchema = z.enum([
    'created',
    'sent',
    'customer_signed',
    'completed',
    'declined',
    'voided',
]);
export const PaymentTermsSourceSchema = z.enum([
    'system_default',
    'account_default',
    'approved_exception',
]);
export const ExpectedCloseConfidenceSchema = z.enum(['low', 'medium', 'high']);
export const CancelReasonSchema = z.enum([
    'customer_declined',
    'no_response',
    'withdrew',
    'price',
    'timing',
    'lost_to_competitor',
    'no_payment',
    'other',
]);
export const CancelInitiatorSchema = z.enum(['customer', 'internal', 'no_payment', 'fraud']);
export const DealSchema = z.object({
    id: IDSchema,
    type: z.literal('deal'),
    schemaVersion: SchemaVersionSchema,
    accountId: IDSchema,
    state: DealStateSchema,
    stateChangedAt: IsoTimestampSchema,
    stateChangedBy: z.string(),
    isPilot: z.boolean().default(false),
    pilotSubState: PilotSubStateSchema.optional(),
    pilotApprovalId: IDSchema.optional(),
    pilotStartDate: IsoTimestampSchema.optional(),
    pilotDay90Date: IsoTimestampSchema.optional(),
    pilotCompletedAt: IsoTimestampSchema.optional(),
    isTrial: z.boolean().default(false),
    trialSubState: TrialSubStateSchema.optional(),
    trialApprovalId: IDSchema.optional(),
    trialStartDate: IsoTimestampSchema.optional(),
    trialEndDate: IsoTimestampSchema.optional(),
    trialCompletedAt: IsoTimestampSchema.optional(),
    trialConvertedDealId: IDSchema.optional(),
    // T4-013b — trial-conversion lineage. Set on the NEW paid Deal that was
    // rep-converted from a trial; points back to the trial Deal. Distinct
    // from `originalDealId` (renewal lineage). The trial Deal itself uses
    // `trialConvertedDealId` to point FORWARD to the new paid Deal.
    originalTrialDealId: IDSchema.optional(),
    eSignVendor: ESignVendorSchema.optional(),
    submissionId: IDSchema.optional(),
    acceptedQuoteId: QuoteIdSchema.optional(),
    envelopeId: z.string().optional(),
    envelopeStatus: EnvelopeStatusSchema.optional(),
    // T5-011 — Vendor envelope URL (DocuSign signing URL). Stamped at
    // envelope-mint time in `acceptSubmission()`. Surfaced on the
    // Submission detail page's awaiting-countersign banner.
    envelopeUrl: z.string().url().optional(),
    msaTemplateVersion: MsaTemplateVersionSchema.optional(),
    msaSignedDocumentId: IDSchema.optional(),
    signedProposalDocumentId: IDSchema.optional(),
    workOrderDocumentId: IDSchema.optional(),
    intakeResponseDocumentId: IDSchema.optional(),
    // T5-012 — Signed envelope + certificate SharePoint refs. Populated by
    // `persistSignedEnvelope()` (Pattern B network-outside-tx service) after
    // T5-009's envelope-completed webhook advances Deal → signed. All four
    // are nullable: Pattern B persist failure does NOT unwind the state
    // change, so a Deal can be `signed` with NULL SharePoint refs (the
    // vendor stays the source of truth in that case).
    signedPdfSharepointFileId: z.string().optional(),
    signedPdfSharepointWebUrl: z.string().optional(),
    certificateSharepointFileId: z.string().optional(),
    certificateSharepointWebUrl: z.string().optional(),
    pricingConfigVersion: z.string(),
    acv: z.number().int().nonnegative(),
    paymentTerms: z.string(),
    paymentTermsSource: PaymentTermsSourceSchema,
    paymentTermsApprovalId: IDSchema.optional(),
    expectedCloseDate: IsoTimestampSchema.optional(),
    expectedCloseConfidence: ExpectedCloseConfidenceSchema.optional(),
    originalDealId: IDSchema.optional(),
    previousRenewalDealId: IDSchema.optional(),
    linkedDealId: IDSchema.optional(),
    renewalCycleNumber: z.number().int().nonnegative().default(0),
    isRenewalStub: z.boolean().default(false),
    proposalSentAt: IsoTimestampSchema.optional(),
    envelopeSentAt: IsoTimestampSchema.optional(),
    customerSignedAt: IsoTimestampSchema.optional(),
    signedAt: IsoTimestampSchema.optional(),
    intakeSubmittedAt: IsoTimestampSchema.optional(),
    activeAt: IsoTimestampSchema.optional(),
    paymentReceivedDate: IsoTimestampSchema.optional(),
    renewalDueDate: IsoTimestampSchema.optional(),
    renewalReminderFiredAt: IsoTimestampSchema.optional(),
    // T7-010 — Per-tier idempotency anchors for the 74d/60d/30d renewal-
    // reminder cron. The legacy `renewalReminderFiredAt` scalar has
    // ambiguous semantics; T7-010 mints 3 typed anchors so each reminder
    // tier dedups independently. Pre-T7-010 Deals omit these fields
    // (nullable / optional); the cron stamps each as its window opens.
    renewalReminderFiredAt74d: IsoTimestampSchema.optional(),
    renewalReminderFiredAt60d: IsoTimestampSchema.optional(),
    renewalReminderFiredAt30dAdmin: IsoTimestampSchema.optional(),
    // T8-002 — portal-welcome email idempotency anchor. Stamped by
    // `firePortalWelcomeEmailOnDealActive` after the email dispatches.
    // Pre-T8-002 Deals omit this field (nullable / optional); the function
    // refuses to re-send when the field is set.
    welcomePortalEmailSentAt: IsoTimestampSchema.optional(),
    cancelledAt: IsoTimestampSchema.optional(),
    cancelledBy: z.string().optional(),
    cancelReason: CancelReasonSchema.optional(),
    cancelInitiator: CancelInitiatorSchema.optional(),
    cancelNote: z.string().optional(),
    reopenedAt: IsoTimestampSchema.optional(),
    reopenedBy: z.string().optional(),
    reopenReason: z.string().optional(),
    history: z.array(HistoryEntrySchema).default([]),
    // RIG-380 — human-readable reference (e.g. `DEAL-01042`). Additive layer over
    // the opaque PK (`id`), which is unchanged. NOT NULL + unique; render via the
    // shared `displayRef`/`displayLabel`, never hand-rolled.
    reference: z.string(),
    createdAt: IsoTimestampSchema,
    createdBy: z.string(),
    updatedAt: IsoTimestampSchema,
    updatedBy: z.string(),
    deletedAt: IsoTimestampSchema.nullable().optional(),
    version: z.number().int().positive(),
});
//# sourceMappingURL=deal.js.map